import { addDays, addMonths, addWeeks, differenceInCalendarDays, format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { Casa, Repeticion, Tarea, TareaHecha } from '../types';

/**
 * Cálculos del módulo Tareas: cuándo vuelve a tocar una tarea, qué cae en
 * cada día de la semana y cómo se dice en palabras. Funciones puras, sin
 * Firestore, para poder probarlas (tareas.test.ts).
 *
 * Las fechas van siempre a mediodía local, como en A medias: así un cambio de
 * hora o de huso no las mueve de día.
 */

export const mediodia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
export const claveDia = (d: Date) => format(d, 'yyyy-MM-dd');
export const mismoDia = (a: Date, b: Date) => claveDia(a) === claveDia(b);
/** Lunes de la semana de `d`, a mediodía. */
export const lunesDe = (d: Date) => mediodia(startOfWeek(d, { weekStartsOn: 1 }));
/** 0 = lunes … 6 = domingo (getDay() empieza en domingo). */
export const diaSemana = (d: Date) => (d.getDay() + 6) % 7;

export const NOMBRES_DIA = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
export const INICIALES_DIA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

/** Tope de vueltas al buscar fechas: una tarea diaria atrasada años no cuelga la app. */
const MAX_PASOS = 2000;

/** Días marcados de una repetición semanal, ordenados; si no hay, el de `fecha`. */
const diasMarcados = (rep: Repeticion, fecha: Date) => {
  const dias = Array.from(new Set((rep.diasSemana ?? []).filter(d => d >= 0 && d <= 6))).sort((a, b) => a - b);
  return dias.length ? dias : [diaSemana(fecha)];
};

/** La vez siguiente a `fecha`, o null si la tarea no se repite. */
export function siguienteFecha(rep: Repeticion, fecha: Date): Date | null {
  const cada = Math.max(1, Math.round(rep.cada || 1));
  const base = mediodia(fecha);
  switch (rep.tipo) {
    case 'una': return null;
    case 'dias': return addDays(base, cada);
    case 'meses': return addMonths(base, cada);
    case 'semanas': {
      const dias = diasMarcados(rep, base);
      const hoyEs = diaSemana(base);
      const mismaSemana = dias.find(d => d > hoyEs);
      if (mismaSemana !== undefined) return addDays(base, mismaSemana - hoyEs);
      // Se acabó la semana: al primer día marcado de dentro de `cada` semanas
      return addDays(addWeeks(lunesDe(base), cada), dias[0]);
    }
  }
}

/**
 * Próxima vez que toca después de `limite` (sin contarlo), contando desde la
 * fecha de la tarea para respetar su ritmo (cada 2 semanas, el día 15…).
 * Null si no se repite.
 */
export function siguienteTras(tarea: Pick<Tarea, 'repeticion' | 'fecha'>, limite: Date): Date | null {
  const tope = claveDia(limite);
  if (tarea.repeticion.tipo === 'dias') {
    // Directo, sin dar vueltas: una diaria atrasada años son miles de pasos
    const cada = Math.max(1, Math.round(tarea.repeticion.cada || 1));
    const dif = differenceInCalendarDays(mediodia(limite), mediodia(tarea.fecha));
    return addDays(mediodia(tarea.fecha), dif < 0 ? cada : (Math.floor(dif / cada) + 1) * cada);
  }
  let f: Date | null = mediodia(tarea.fecha);
  for (let i = 0; f && i < MAX_PASOS; i++) {
    f = siguienteFecha(tarea.repeticion, f);
    if (f && claveDia(f) > tope) return f;
  }
  return f;
}

/**
 * La fecha que le queda a una tarea al hacerla (o saltarla) hoy: la siguiente
 * después de la que tocaba o de hoy, lo que sea más tarde. Así, si la hiciste
 * con retraso no te quedan pendientes las de días ya pasados, y si la
 * adelantas cuenta como la que tocaba. Null si no se repite (queda terminada).
 */
export function fechaTrasHacer(tarea: Pick<Tarea, 'repeticion' | 'fecha'>, hoy: Date = new Date()): Date | null {
  const limite = claveDia(tarea.fecha) > claveDia(hoy) ? tarea.fecha : hoy;
  return siguienteTras(tarea, limite);
}

/** Si una tarea pendiente ya debería estar hecha. */
export const estaAtrasada = (tarea: Pick<Tarea, 'fecha' | 'activa'>, hoy: Date = new Date()) =>
  tarea.activa && claveDia(tarea.fecha) < claveDia(hoy);

export type EstadoOcurrencia = 'pendiente' | 'futura' | 'hecha' | 'saltada';

/** Algo que cae en un día de la semana. */
export interface Ocurrencia {
  clave: string;
  tarea: Tarea;
  fecha: Date;
  /**
   * pendiente: la próxima de la tarea, la que se puede marcar.
   * futura: las siguientes de la semana, para ver lo que viene.
   * hecha / saltada: del historial.
   */
  estado: EstadoOcurrencia;
  hecha?: TareaHecha;
}

/**
 * Lo que cae entre `desde` y `hasta` (ambos incluidos): la próxima de cada
 * tarea, las que vendrán después y lo ya hecho. Las atrasadas no salen aquí
 * (su fecha es anterior): van aparte, con estaAtrasada().
 */
export function ocurrencias(
  tareas: Tarea[], hechas: TareaHecha[], desde: Date, hasta: Date, hoy: Date = new Date(),
): Ocurrencia[] {
  const ini = claveDia(desde);
  const fin = claveDia(hasta);
  const dentro = (d: Date) => { const k = claveDia(d); return k >= ini && k <= fin; };
  const lista: Ocurrencia[] = [];

  for (const tarea of tareas) {
    if (!tarea.activa) continue;
    if (dentro(tarea.fecha)) {
      lista.push({ clave: `${tarea.id}:${claveDia(tarea.fecha)}`, tarea, fecha: mediodia(tarea.fecha), estado: 'pendiente' });
    }
    // Las que vendrán: a partir de la que quedará al hacer la pendiente.
    // Si va por turnos, cada una le toca al siguiente.
    let f = fechaTrasHacer(tarea, hoy);
    for (let i = 0; f && claveDia(f) <= fin && i < MAX_PASOS; i++) {
      if (dentro(f)) {
        const asignadoA = tarea.turno ? siguienteEnTurno(tarea.turno, tarea.asignadoA, i + 1) : tarea.asignadoA;
        lista.push({ clave: `${tarea.id}:${claveDia(f)}`, tarea: { ...tarea, asignadoA }, fecha: f, estado: 'futura' });
      }
      f = siguienteFecha(tarea.repeticion, f);
    }
  }

  const porId = new Map(tareas.map(t => [t.id, t]));
  for (const h of hechas) {
    // Lo hecho cuenta el día que se hizo, no el que tocaba
    if (!dentro(h.hechaEn)) continue;
    const tarea = porId.get(h.tareaId) ?? tareaDeHecha(h);
    lista.push({ clave: `h:${h.id}`, tarea, fecha: mediodia(h.hechaEn), estado: h.saltada ? 'saltada' : 'hecha', hecha: h });
  }

  const orden = { pendiente: 0, futura: 1, hecha: 2, saltada: 3 };
  return lista.sort((a, b) =>
    a.fecha.getTime() - b.fecha.getTime()
    || orden[a.estado] - orden[b.estado]
    || (a.tarea.hora ?? '99').localeCompare(b.tarea.hora ?? '99')
    || a.tarea.nombre.localeCompare(b.tarea.nombre, 'es'));
}

/** Una tarea ya borrada, reconstruida con lo que guarda su historial. */
const tareaDeHecha = (h: TareaHecha): Tarea => ({
  id: h.tareaId, casaId: h.casaId, nombre: h.nombreTarea, icono: h.icono,
  repeticion: { tipo: 'una', cada: 1 }, fecha: h.fecha, esfuerzo: 1, activa: false,
  creadoPorUid: h.uid, createdAt: h.hechaEn, updatedAt: h.hechaEn,
});

/** «Cada día», «Lunes y jueves», «Cada 2 semanas, el sábado», «Cada mes»… */
export function describirRepeticion(rep: Repeticion, fecha?: Date): string {
  const cada = Math.max(1, Math.round(rep.cada || 1));
  switch (rep.tipo) {
    case 'una': return 'Una vez';
    case 'dias': return cada === 1 ? 'Cada día' : `Cada ${cada} días`;
    case 'meses': {
      const dia = fecha ? `, el día ${fecha.getDate()}` : '';
      // El mantenimiento suele ir por años: «Cada año» se lee mejor que «Cada 12 meses»
      if (cada % 12 === 0) return (cada === 12 ? 'Cada año' : `Cada ${cada / 12} años`) + (fecha ? `, el ${fecha.getDate()} de ${format(fecha, 'MMMM', { locale: es })}` : '');
      return (cada === 1 ? 'Cada mes' : `Cada ${cada} meses`) + dia;
    }
    case 'semanas': {
      const dias = diasMarcados(rep, fecha ?? new Date());
      if (dias.length === 7 && cada === 1) return 'Cada día';
      const nombres = dias.map(d => NOMBRES_DIA[d]);
      const lista = nombres.length === 1 ? nombres[0]
        : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
      if (cada === 1) return lista[0].toUpperCase() + lista.slice(1);
      return `Cada ${cada} semanas: ${lista}`;
    }
  }
}

/** «Hoy», «Mañana», «Ayer», «Hace 3 días», «El jueves 6». */
export function describirDia(fecha: Date, hoy: Date = new Date()): string {
  const dif = differenceInCalendarDays(fecha, hoy);
  if (dif === 0) return 'Hoy';
  if (dif === 1) return 'Mañana';
  if (dif === -1) return 'Ayer';
  if (dif < 0) return `Hace ${-dif} días`;
  if (dif < 7) return format(fecha, "'El' EEEE d", { locale: es });
  return format(fecha, "d 'de' MMMM", { locale: es });
}

export const ESFUERZOS: { valor: 1 | 2 | 3; nombre: string }[] = [
  { valor: 1, nombre: 'Poco' },
  { valor: 2, nombre: 'Medio' },
  { valor: 3, nombre: 'Mucho' },
];

export const ZONAS = ['Cocina', 'Baño', 'Salón', 'Dormitorio', 'Lavadero', 'Exterior', 'General'];

/**
 * Color de cada persona de la casa, por su posición. Clases completas (no
 * montadas con plantillas) para que Tailwind las encuentre al compilar.
 */
export const COLORES_PERSONA = [
  { punto: 'bg-blue-500', suave: 'bg-blue-50 dark:bg-blue-900/30', texto: 'text-blue-700 dark:text-blue-300', borde: 'border-l-blue-500' },
  { punto: 'bg-emerald-500', suave: 'bg-emerald-50 dark:bg-emerald-900/30', texto: 'text-emerald-700 dark:text-emerald-300', borde: 'border-l-emerald-500' },
  { punto: 'bg-purple-500', suave: 'bg-purple-50 dark:bg-purple-900/30', texto: 'text-purple-700 dark:text-purple-300', borde: 'border-l-purple-500' },
  { punto: 'bg-orange-500', suave: 'bg-orange-50 dark:bg-orange-900/30', texto: 'text-orange-700 dark:text-orange-300', borde: 'border-l-orange-500' },
  { punto: 'bg-cyan-500', suave: 'bg-cyan-50 dark:bg-cyan-900/30', texto: 'text-cyan-700 dark:text-cyan-300', borde: 'border-l-cyan-500' },
  { punto: 'bg-red-500', suave: 'bg-red-50 dark:bg-red-900/30', texto: 'text-red-700 dark:text-red-300', borde: 'border-l-red-500' },
  { punto: 'bg-amber-500', suave: 'bg-amber-50 dark:bg-amber-900/30', texto: 'text-amber-700 dark:text-amber-300', borde: 'border-l-amber-500' },
];
export const COLOR_SIN_ASIGNAR = {
  punto: 'bg-gray-400', suave: 'bg-gray-100 dark:bg-gray-700/50', texto: 'text-gray-600 dark:text-gray-300', borde: 'border-l-gray-300 dark:border-l-gray-600',
};

export function colorDe(casa: Casa | undefined, participanteId: string | undefined) {
  if (!casa || !participanteId) return COLOR_SIN_ASIGNAR;
  const i = casa.participantes.findIndex(p => p.id === participanteId);
  return i < 0 ? COLOR_SIN_ASIGNAR : COLORES_PERSONA[i % COLORES_PERSONA.length];
}

/**
 * A quién le toca `pasos` vueltas después de `actual` en un turno. Si `actual`
 * no está en el turno, empieza por el primero. Sin turno, sigue `actual`.
 */
export function siguienteEnTurno(turno: string[] | undefined, actual: string | undefined, pasos = 1): string | undefined {
  if (!turno || turno.length === 0) return actual;
  const i = actual ? turno.indexOf(actual) : -1;
  if (i < 0) return turno[(pasos - 1) % turno.length];
  return turno[(i + pasos) % turno.length];
}

/** El turno sin quien ya no está en la casa o está desactivado. */
export const turnoVigente = (turno: string[] | undefined, casa: Casa | undefined) =>
  (turno ?? []).filter(id => casa?.participantes.some(p => p.id === id && p.activo));

/** Mi participante en una casa, si ya he dicho quién soy. */
export const yoEn = (casa: Casa | undefined, uid: string | undefined) =>
  casa && uid ? casa.participantes.find(p => p.uid === uid) : undefined;

/** Si una tarea es mía: me la asignaron, o es de mi casa personal. */
export const esMia = (tarea: Tarea, casa: Casa | undefined, uid: string | undefined) =>
  !!casa && (casa.personal || (!!tarea.asignadoA && tarea.asignadoA === yoEn(casa, uid)?.id));

/**
 * Si una tarea es para avisarme: las mías y, en las casas compartidas, las de
 * «Cualquiera» (sin nadie asignado), que también me pueden tocar a mí.
 */
export const meAvisa = (tarea: Tarea, casa: Casa | undefined, uid: string | undefined) =>
  esMia(tarea, casa, uid) || (!!casa && !casa.personal && !tarea.asignadoA && !!yoEn(casa, uid));

/** Tareas que se repiten y siguen activas: las que cuentan para el límite gratis. */
export const tareasQueSeRepiten = (tareas: Tarea[], casaId: string) =>
  tareas.filter(t => t.casaId === casaId && t.activa && t.repeticion.tipo !== 'una').length;

/** Casas compartidas que organizas: las que cuentan para el límite gratis. */
export const casasCompartidasOrganizadas = (casas: Casa[], uid: string | undefined) =>
  casas.filter(c => !c.personal && !!uid && c.organizadoresUids.includes(uid)).length;

/** Primera vez que toca una tarea nueva: hoy, o el próximo día marcado si es semanal. */
export function primeraFecha(rep: Repeticion, desde: Date = new Date()): Date {
  const base = mediodia(desde);
  if (rep.tipo !== 'semanas') return base;
  const dias = diasMarcados(rep, base);
  const hoyEs = diaSemana(base);
  const proximo = dias.find(d => d >= hoyEs) ?? dias[0] + 7;
  return addDays(base, proximo - hoyEs);
}
